import { PokerVariant, TableType } from '@repo/database/src/types';

import { ComputedGameParams } from './dto/table/game-params.dto';
import { TournamentTableStateDto } from './dto/tournaments/tournament.dto';
import { OFCPlayerStateDto, OFCScoringResults } from './ofc-types';
import { AvailableActions, HandState, Operation } from './table-hand-types';
import { AllInEquityDto, PlayerSeatDto, SeatDto } from './table-types';

/**
 * Unified table state DTO for both poker and OFC tables.
 *
 * Poker-specific fields (handState, currentVariant, etc.) are present for poker tables.
 * OFC-specific fields (ofcPointValue, category, playerState, etc.) are present for OFC tables.
 * Use `category === 'Chinese'` to determine if this is an OFC table.
 */
export interface TableStateDto {
  tableId: string;
  handId: string | null;
  name: string;
  game: string;
  type: TableType;
  ante: number | null;
  smallBlind: number | null;
  bigBlind: number | null;
  maxPlayers: number;
  minBuyin: number | null;
  maxBuyin: number | null;
  smallBet: number | null;
  bigBet: number | null;
  bringIn: number | null;
  bettingCap: number | null;
  dealerSeat: number | null;
  stateVersion?: number;
  baseStateVersion?: number;
  newVersionAvailable?: boolean;
  previousVariant?: PokerVariant;
  currentVariant: PokerVariant;
  currentVariantIndex: number;
  handState?: {
    handId: string | null;
    state?: HandState;
    availableActions: AvailableActions | null;
    lastOperation: Operation | null;
  };
  gameParams: ComputedGameParams[];
  seats: (SeatDto | null)[];
  waitlist: string[];
  playerSeat?: PlayerSeatDto | null;
  currentDraw?: number;
  totalDraws?: number;
  handsInCurrentGame: number;
  handsPerGame: number;
  variants: PokerVariant[];
  contractId: number | null;
  contractAddress?: `0x${string}`;
  isRealNameTable: boolean;
  lobbyIcons: string[];
  url: string;
  tournamentState?: Partial<TournamentTableStateDto> | null;
  allInEquity?: AllInEquityDto;

  // OFC-specific fields (optional, only present for OFC tables)
  ofcPointValue?: number;
  category?: string;
  playerState?: OFCPlayerStateDto | null;
  fantasyPlayers?: string[];
  actionDeadlines?: Record<string, number>;
  currentDeadline?: number | null;
  boardsRevealed?: boolean;
  scoringResults?: OFCScoringResults | null;
  fantasyLockActive?: boolean;
}

export interface TableJoinedDto {
  userId: string;
  tableId: string;
  seat: SeatDto;
  playerSeat?: PlayerSeatDto | null;
}
